import React from "react";
import { Auth } from "@aws-amplify/auth";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth/lib/types";
import { CognitoUser } from "amazon-cognito-identity-js";
import axios from "axios";

interface CognitoContextInterface {
  loading: boolean;
  fetchAuthenticatedUser: () => void;
  signedIn: boolean;
  setCognitoState: () => CognitoState;
  signInApple: () => Promise<CognitoUser>;
  signInGoogle: () => Promise<CognitoUser>;
  signOut: () => Promise<any>;
  user: User | null;
  isFetchingUserFromDb: boolean;
}

const defaultContext: CognitoContextInterface = {
  loading: true,
  fetchAuthenticatedUser: () => new Promise(() => {}),
  signedIn: false,
  user: null,
  setCognitoState: () => defaultState,
  signInApple: () => new Promise(() => {}),
  signInGoogle: () => new Promise(() => {}),
  signOut: () => new Promise(() => {}),
  isFetchingUserFromDb: true
};

const CognitoContext = React.createContext<CognitoContextInterface | undefined>(
  undefined
);

interface CognitoState {
  loading: boolean;
  signedIn: boolean;
}

export interface User {
  id: string;
  access_token: string;
  email: string;
}

const defaultState = {
  signedIn: false,
  loading: true
};

// Cognito access tokens live ~1h. Refresh well before that so a long-lived,
// actively used tab never sends an expired token.
const TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export function CognitoProvider(props: any) {
  const [state, setState] = React.useState<CognitoState>(defaultState);
  const [user, setUser] = React.useState<User | null>(null);
  const [isFetchingUserFromDb, setIsFetchingUserFromDb] =
    React.useState<boolean>(true);

  const hostname = process.env.REACT_APP_API_CORE_URL?.replace(
    /https?:\/\//,
    ""
  ).replace(/:\d+/, "");

  // Uncomment this code cognito useMemo to directly use dev api (without local backend)
  // and comment the other cognito useMemo. (production useMemo is below this one)
  // Then when you finish working, comment this back and uncomment the other one.
  // it is only for development purposes, do not forget to uncomment real cognito useMemo.
  const cognito = React.useMemo(() => {
    const webHostname = document.location.hostname;
    const apiHostname = hostname || webHostname;

    const isIpAddress = (h: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(h);
    const isLocalHost = (h: string) =>
      h === "localhost" ||
      h === "127.0.0.1" ||
      h.endsWith(".local") ||
      isIpAddress(h);

    const getSharedBaseDomain = (a: string, b: string): string | null => {
      const aParts = a.split(".").reverse();
      const bParts = b.split(".").reverse();
      const common: string[] = [];
      for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
        if (aParts[i] === bParts[i]) {
          common.push(aParts[i]);
        } else {
          break;
        }
      }
      return common.length >= 2 ? common.reverse().join(".") : null;
    };

    const sharedDomain = getSharedBaseDomain(webHostname, apiHostname);
    const cookieDomain =
      isLocalHost(webHostname) || isLocalHost(apiHostname)
        ? webHostname
        : sharedDomain
        ? `.${sharedDomain}`
        : `.${webHostname}`;

    const secureFlag = document.location.protocol === "https:";

    Auth.configure({
      Auth: {
        region: process.env.REACT_APP_AWS_REGION,
        userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
        oauth: {
          domain: process.env.REACT_APP_COGNITO_DOMAIN,
          redirectSignIn: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN,
          redirectSignOut: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT,
          scope: ["email", "openid"],
          clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
          responseType: "code"
        }
      },
      Analytics: {
        disabled: true
      },
      cookieStorage: {
        domain: cookieDomain,
        secure: secureFlag,
        path: "/",
        expires: 365
      }
    });

    return Auth;
  }, [hostname]);

  // const cognito = React.useMemo(() => {
  //   Auth.configure({
  //     Auth: {
  //       region: process.env.REACT_APP_AWS_REGION,
  //       userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  //       userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  //       oauth: {
  //         domain: process.env.REACT_APP_COGNITO_DOMAIN,
  //         redirectSignIn: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN,
  //         redirectSignOut: process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT,
  //         scope: ["email", "openid"],
  //         clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  //         responseType: "code"
  //       }
  //     },
  //     Analytics: {
  //       disabled: true
  //     },
  //     cookieStorage: {
  //       domain:
  //         document.location.hostname === hostname
  //           ? hostname
  //           : ".pointmentapp.com",
  //       secure: document.location.hostname !== hostname,
  //       path: "/",
  //       expires: 365
  //     }
  //   });

  //   return Auth;
  // }, [hostname]);

  const fetchAuthenticatedUser = React.useCallback(async () => {
    setIsFetchingUserFromDb(true);
    try {
      const user = await cognito.currentAuthenticatedUser();
      const idTokenPayload = user.signInUserSession.idToken.payload;
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_CORE_URL}/v1/users/${idTokenPayload.email}`,
        {
          headers: {
            Authorization: `Bearer ${user.signInUserSession.accessToken.jwtToken}`
          }
        }
      );
      if (!data || data.role.role === "USER") {
        await cognito.signOut();
        setUser(null);
        setState({ loading: false, signedIn: false });
        return (window.location.href =
          process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || "");
      }

      setUser({
        id: idTokenPayload.sub,
        access_token: user.signInUserSession.accessToken.jwtToken,
        email: idTokenPayload.email
      });

      setState({
        loading: false,
        signedIn: true
      });
    } catch (error) {
      setState({ loading: false, signedIn: false });
    } finally {
      // We need this code because, when user is registering with google/apple or
      // the user is created in Cognito but we don't have that user in the database.
      // (this happens because the DEV and LOCAL environments are using the same user pool).
      // Basically when user was not found in database and we need to
      // redirect them to UserName component on "/on-boarding/name" route.
      // Because we need to handle this cases, "/on-boarding/name" always appears after login.
      // because the real loading state is not enough. I added 1 second to the real loading time.
      // With this, users will not be interrupted by this unnecessary screen on every login.
      // If users need to see this screen, they will see it after loading time + 1 second.
      setTimeout(() => {
        setIsFetchingUserFromDb(false);
      }, 1000);
    }
  }, [cognito]);

  React.useEffect(() => {
    fetchAuthenticatedUser();
  }, [fetchAuthenticatedUser]);

  // Keep the stored access token fresh. The token captured at sign-in expires
  // after ~1h; without proactive refresh it goes stale while the tab is idle
  // (or the machine sleeps) and every request 401s until the user reloads the
  // page. Auth.currentSession() transparently mints a new access token from the
  // longer-lived refresh token, so we re-derive state from it on an interval and
  // whenever the user returns to a backgrounded tab.
  React.useEffect(() => {
    if (!state.signedIn) return;

    const refreshSession = async () => {
      try {
        const session = await cognito.currentSession();
        const accessToken = session?.getAccessToken()?.getJwtToken();
        if (!accessToken) return;
        setUser((prev) =>
          prev && prev.access_token !== accessToken
            ? { ...prev, access_token: accessToken }
            : prev
        );
      } catch (error) {
        // Refresh token is missing/expired/revoked -> the session can't be
        // restored. Reset auth state so the app routes back to sign-in instead
        // of silently firing requests with a dead token.
        setUser(null);
        setState({ loading: false, signedIn: false });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSession();
      }
    };

    const interval = setInterval(refreshSession, TOKEN_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", refreshSession);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", refreshSession);
    };
  }, [cognito, state.signedIn]);

  const signInApple = () =>
    cognito.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Apple
    });

  const signInGoogle = () =>
    cognito.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google
    });

  const signOut = async (): Promise<any> => {
    await cognito.signOut();
    setUser(null);
    setState({ loading: false, signedIn: false });
    localStorage.clear();
  };

  return (
    <CognitoContext.Provider
      value={{
        fetchAuthenticatedUser,
        setCognitoState: setState,
        signInApple,
        signInGoogle,
        signOut,
        user,
        isFetchingUserFromDb,
        ...state
      }}
      {...props}
    />
  );
}

export function useCognito() {
  const context = React.useContext(CognitoContext);

  if (context === undefined) {
    throw new Error("useCognito must be used within a CognitoProvider.");
  }

  return context;
}

export async function getToken() {
  try {
    if (Auth) {
      const session = await Auth.currentSession();

      return session?.getAccessToken()?.getJwtToken();
    }

    return false;
  } catch (error: any) {
    return error.message;
  }
}

export function TestProvider({
  value,
  children
}: {
  value?: Partial<CognitoContextInterface>;
  children: React.ReactNode;
}) {
  return (
    <CognitoContext.Provider value={{ ...defaultContext, ...value }}>
      {children}
    </CognitoContext.Provider>
  );
}
